<script setup>
import { ref, watch } from 'vue'
import axios from 'axios'
import useNotifications from '@/store/notification.js'
import { useCompanyInfoStore } from '@/store/company'

const { showNotification } = useNotifications()
const companyStore = useCompanyInfoStore()
const companyInfo = ref(null)

companyStore.fetchCompanyInfo()
watch(
  () => companyStore.companyInfo,
  (newInfo) => { if (newInfo) companyInfo.value = newInfo },
  { immediate: true }
)

const name = ref('')
const email = ref('')
const message = ref('')
const loading = ref(false)
const api_base_url = import.meta.env.VITE_API_BASE_URL

const handleSubmit = async () => {
  if (!name.value || !email.value || !message.value) return
  try {
    loading.value = true
    const response = await axios.post(`${api_base_url}/api/contact/`, {
      name: name.value,
      email: email.value,
      message: message.value,
    })
    if (response.status === 200 || response.status === 201) {
      showNotification('Success', 'Message sent successfully! We will get back to you shortly.', 'success')
      name.value = ''
      email.value = ''
      message.value = ''
    }
  } catch {
    showNotification('Error', 'An error occurred while sending the message. Please try again later.', 'error')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="bg-theme-bg min-h-screen">
    <div class="max-w-5xl mx-auto px-4 pt-12 pb-16 sm:pt-16 sm:pb-20">
      <!-- Header -->
      <div class="text-center mb-10">
        <span class="inline-flex items-center gap-1.5 bg-primary-50 dark:bg-primary-600/10 text-primary-600 dark:text-primary-400 text-xs font-semibold px-3 py-1 rounded-full mb-4">
          <i class="pi pi-envelope text-[10px]"></i> Contact
        </span>
        <h1 class="text-3xl sm:text-4xl font-extrabold text-theme-text tracking-tight mb-3">
          Get In Touch
        </h1>
        <p class="text-base text-theme-text2 max-w-xl mx-auto">
          Have a question or need help? We'd love to hear from you.
        </p>
      </div>

      <div class="grid lg:grid-cols-5 gap-6">
        <!-- Contact Info Sidebar -->
        <div class="lg:col-span-2 space-y-4">
          <!-- Email -->
          <div v-if="companyInfo?.email" class="bg-theme-card border border-theme-border rounded-2xl p-5 flex items-start gap-4">
            <div class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-600/10 flex items-center justify-center shrink-0">
              <i class="pi pi-envelope text-primary-600"></i>
            </div>
            <div>
              <p class="text-sm font-semibold text-theme-text mb-0.5">Email</p>
              <a :href="`mailto:${companyInfo.email}`" class="text-sm text-primary-600 hover:underline break-all">
                {{ companyInfo.email }}
              </a>
            </div>
          </div>

          <!-- Phone -->
          <div v-if="companyInfo?.phone_number" class="bg-theme-card border border-theme-border rounded-2xl p-5 flex items-start gap-4">
            <div class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-600/10 flex items-center justify-center shrink-0">
              <i class="pi pi-phone text-primary-600"></i>
            </div>
            <div>
              <p class="text-sm font-semibold text-theme-text mb-0.5">Phone</p>
              <a :href="`tel:${companyInfo.phone_number.replace(/\s+/g, '')}`" class="text-sm text-primary-600 hover:underline">
                {{ companyInfo.phone_number }}
              </a>
            </div>
          </div>

          <!-- Address -->
          <div v-if="companyInfo?.address" class="bg-theme-card border border-theme-border rounded-2xl p-5 flex items-start gap-4">
            <div class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-600/10 flex items-center justify-center shrink-0">
              <i class="pi pi-map-marker text-primary-600"></i>
            </div>
            <div>
              <p class="text-sm font-semibold text-theme-text mb-0.5">Address</p>
              <p class="text-sm text-theme-text2">{{ companyInfo.address }}</p>
            </div>
          </div>

          <!-- Hours -->
          <div class="bg-theme-card border border-theme-border rounded-2xl p-5 flex items-start gap-4">
            <div class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-600/10 flex items-center justify-center shrink-0">
              <i class="pi pi-clock text-primary-600"></i>
            </div>
            <div>
              <p class="text-sm font-semibold text-theme-text mb-0.5">Availability</p>
              <p class="text-sm text-theme-text2">24/7 — We're always here to help</p>
            </div>
          </div>
        </div>

        <!-- Contact Form -->
        <div class="lg:col-span-3">
          <form @submit.prevent="handleSubmit" class="bg-theme-card border border-theme-border rounded-2xl p-6 sm:p-8 space-y-5">
            <div>
              <label for="name" class="block text-sm font-semibold text-theme-text mb-1.5">Your Name</label>
              <input type="text" id="name" v-model="name" required
                class="w-full px-4 py-3 bg-theme-input border border-theme-border text-theme-text rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                placeholder="John Doe" />
            </div>
            <div>
              <label for="email" class="block text-sm font-semibold text-theme-text mb-1.5">Your Email</label>
              <input type="email" id="email" v-model="email" required
                class="w-full px-4 py-3 bg-theme-input border border-theme-border text-theme-text rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                placeholder="john@example.com" />
            </div>
            <div>
              <label for="message" class="block text-sm font-semibold text-theme-text mb-1.5">Message</label>
              <textarea id="message" v-model="message" required rows="5"
                class="w-full px-4 py-3 bg-theme-input border border-theme-border text-theme-text rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition resize-none"
                placeholder="How can we help?"></textarea>
            </div>
            <button type="submit" :disabled="loading"
              class="w-full py-3 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-500 transition text-sm disabled:opacity-50 flex items-center justify-center gap-2">
              <i v-if="loading" class="pi pi-spinner pi-spin text-sm"></i>
              <span>{{ loading ? 'Sending...' : 'Send Message' }}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>